'use client';

import { useCallback } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { deleteChannelMessage } from '../functions/message/deleteChannelMessage';

/**
 * Soft-delete a channel message. The bubble's "deleted" state flips
 * to the placeholder via the existing Firestore message
 * subscription.
 *
 * `creatorId` is the STORE owner (the `stores/{creatorId}/...` root the
 * message doc lives under) from the community context — NOT the acting
 * viewer. A regular member deleting their own message is not the store
 * owner, so scoping the call to `useCurrentUserId()` targeted a store
 * they don't own and silently no-op'd.
 */
export function useDeleteChannelMessage({
  channelId,
  messageId,
}: {
  channelId: string | null | undefined;
  messageId: string | null | undefined;
}) {
  const { creatorId } = useCommunityContext();
  const currentUserId = useCurrentUserId();
  return useCallback(async () => {
    if (!creatorId || !currentUserId || !channelId || !messageId) return;
    await deleteChannelMessage({
      creatorId,
      channelId,
      messageId,
    });
  }, [creatorId, currentUserId, channelId, messageId]);
}
